import { supabase, clearAuthSession } from "@/lib/supabase";
import { handleError, ErrorContext } from "@/lib/utils/error-handler";
import { isEmailVerified } from "@/lib/utils/auth-helpers";
import { getSupabaseErrorMessage, getSupabaseErrorKey } from "@/lib/utils/supabase-error-messages";
import { retryAuthOperation, retrySessionOperation } from "@/lib/utils/retry-utils";
import { logAuthError, createLogContext } from "@/lib/utils/logger";
import { accountDeletionService } from "./account-deletion.service";
import type { HeardFromValue } from "@/constants/heard-from";
import {
  buildEmailConfirmRedirect,
  recallPostVerifyRedirect,
} from "@/lib/utils/post-verify-redirect";
import { rememberSignupRole } from "@/lib/utils/signup-landing";

export interface SignUpData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  agency_name?: string; // Pour les agences (role = 'owner')
  role?: "renter" | "owner" | "admin";
  /** Version des CGU acceptées à l'inscription (preuve juridique) */
  terms_version?: string;
  /** Date d'acceptation des CGU au format ISO (preuve juridique) */
  terms_accepted_at?: string;
  /** Canal d'acquisition (optionnel), recopié dans profiles.heard_from */
  heard_from?: HeardFromValue;
  /** Téléphone / WhatsApp optionnel (agences). Nullable côté profil. */
  phone?: string;
  /** Ville agence (metadata only — no schema change). */
  city?: string;
  /** Taille de flotte déclarée (1-5 | 6-20 | 20+). */
  fleet_size?: string;
  /** Internal path to resume after the verification email (car, search, reserve). */
  redirectTo?: string | null;
}

export interface SignInData {
  email: string;
  password: string;
}

/**
 * Get the base URL for redirects
 *
 * Détecte automatiquement l'URL selon l'environnement :
 * - Développement local (DEV) : utilise TOUJOURS window.location.origin (localhost) pour éviter les redirections vers Vercel
 * - Production : utilise VITE_APP_URL si défini, sinon window.location.origin
 *
 * Valide le format de l'URL avant de la retourner et log en dev pour faciliter le debugging.
 *
 * @returns URL de base validée pour les redirections
 */
function getBaseUrl(): string {
  // EN DÉVELOPPEMENT LOCAL : Toujours utiliser window.location.origin pour forcer localhost
  // Cela évite les redirections vers Vercel même si VITE_APP_URL est défini
  if (import.meta.env.DEV) {
    if (typeof window !== "undefined" && window.location) {
      const origin = window.location.origin;

      // Valider que c'est une URL valide
      try {
        new URL(origin);

        // Logger en dev pour faciliter le debugging
        console.log("[Auth] DEV mode: Using window.location.origin:", origin);
        console.log("[Auth] Full redirect URL will be:", `${origin}/auth/callback`);

        return origin;
      } catch {
        console.error("[Auth] Invalid window.location.origin, using fallback");
      }
    }

    // Fallback pour dev si window n'est pas disponible
    const fallback = "http://localhost:5173";
    console.warn("[Auth] DEV mode: Using fallback URL:", fallback);
    return fallback;
  }

  // EN PRODUCTION : Utiliser VITE_APP_URL si défini, sinon window.location.origin
  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl) {
    // Valider le format de l'URL
    try {
      new URL(appUrl);
      console.log("[Auth] PROD mode: Using VITE_APP_URL:", appUrl);
      return appUrl;
    } catch {
      console.warn("[Auth] Invalid VITE_APP_URL format, falling back to window.location.origin");
    }
  }

  // Fallback à window.location.origin en production si VITE_APP_URL n'est pas défini
  if (typeof window !== "undefined" && window.location) {
    const origin = window.location.origin;
    try {
      new URL(origin);
      console.log("[Auth] PROD mode: Using window.location.origin:", origin);
      return origin;
    } catch {
      console.error("[Auth] Invalid window.location.origin, using fallback");
    }
  }

  // Fallback sécurisé (ne devrait jamais arriver en contexte navigateur)
  const fallback = "http://localhost:3000";
  console.warn("[Auth] Using fallback URL:", fallback);
  return fallback;
}

export const authService = {
  /**
   * Sign up a new user
   * Note: Email confirmation is now enabled in Supabase Dashboard
   * (Authentication > Settings > Email Auth > Enable "Enable email confirmations")
   */
  async signUp(data: SignUpData) {
    const role = data.role || "renter";

    // Préparer les metadata selon le rôle
    const userMetadata: Record<string, any> = {
      role: role,
    };

    if (role === "owner") {
      // Pour les agences, utiliser agency_name + first_name/last_name du propriétaire
      if (data.agency_name) {
        userMetadata.agency_name = data.agency_name;
      }
      // Également stocker le prénom/nom du propriétaire pour les contrats et signatures
      if (data.first_name) {
        userMetadata.first_name = data.first_name;
      }
      if (data.last_name) {
        userMetadata.last_name = data.last_name;
      }
    } else {
      // Pour les renters, utiliser first_name et last_name
      if (data.first_name) {
        userMetadata.first_name = data.first_name;
      }
      if (data.last_name) {
        userMetadata.last_name = data.last_name;
      }
    }

    if (data.terms_version) {
      userMetadata.terms_version = data.terms_version;
    }
    if (data.terms_accepted_at) {
      userMetadata.terms_accepted_at = data.terms_accepted_at;
    }
    if (data.heard_from) {
      userMetadata.heard_from = data.heard_from;
    }
    if (data.phone) {
      userMetadata.phone = data.phone;
      userMetadata.whatsapp_number = data.phone;
    }
    if (data.city) {
      userMetadata.city = data.city;
    }
    if (data.fleet_size) {
      userMetadata.fleet_size = data.fleet_size;
    }

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        // PKCE: /auth/confirm exchanges the token, then resumes redirectTo or /search.
        emailRedirectTo: buildEmailConfirmRedirect(getBaseUrl(), data.redirectTo),
        data: userMetadata,
      },
    });

    // Log verification email sent after successful signup
    if (!authError && authData.user) {
      rememberSignupRole(role);
      try {
        await supabase.from("email_verification_audit").insert({
          user_id: authData.user.id,
          action: "verification_sent",
          metadata: {
            email: data.email,
            role: role,
            resend: false,
          },
        });
      } catch (auditError) {
        // Non-critical: log error but don't block signup
        logAuthError(
          "Error logging verification sent to audit",
          createLogContext(authData.user.id, "signUp", "authService"),
          auditError instanceof Error ? auditError : undefined
        );
      }
    }

    if (authError) {
      // Logger les codes d'erreur pour le debugging
      const errorCode = (authError as { code?: string }).code;
      const errorStatus = (authError as { status?: number }).status;

      // Logger les erreurs en dev pour le debugging
      if (import.meta.env.DEV) {
        logAuthError(
          "SignUp error",
          createLogContext(undefined, "signUp", "authService", {
            message: authError.message,
            code: errorCode,
            status: errorStatus,
            email: data.email,
          }),
          authError instanceof Error ? authError : undefined
        );
      }

      const context: ErrorContext = {
        action: "signUp",
        component: "authService",
        email: data.email,
        role: role,
        // Ajouter les codes d'erreur au contexte
        ...(errorCode && { errorCode }),
        ...(errorStatus && { errorStatus }),
      };
      handleError(authError, context, { showToast: false, silent: false });
      // Utiliser une clé i18n pour que l'UI affiche t(key) dans la langue courante
      const errorKey = getSupabaseErrorKey(authError, "errors.auth.signupFailed");
      throw new Error(errorKey);
    }

    if (!authData.user) {
      throw new Error("Failed to create user");
    }

    // Détecter la réponse obfusquée de Supabase (email déjà utilisé via OAuth)
    // Supabase retourne un user sans erreur mais avec identities vide pour prévenir
    // les attaques d'énumération d'utilisateurs. Dans ce cas, aucun email n'est envoyé.
    //
    // Indicateurs de réponse obfusquée:
    // 1. identities est vide ou undefined
    // 2. L'utilisateur existe mais n'a pas la nouvelle identité email
    const isObfuscatedResponse = this.isObfuscatedSignUpResponse(authData.user, data.email);

    if (isObfuscatedResponse) {
      if (import.meta.env.DEV) {
        logAuthError(
          "Obfuscated signup response detected - email likely exists with OAuth",
          createLogContext(undefined, "signUp", "authService", {
            email: data.email,
            identitiesCount: authData.user.identities?.length ?? 0,
          })
        );
      }

      // Lancer une erreur spécifique pour ce cas
      throw new Error("EMAIL_EXISTS_WITH_DIFFERENT_PROVIDER");
    }

    // Note: Le profil est créé automatiquement par handle_new_user via le trigger
    // On ne fait plus d'insertion manuelle ici pour éviter les conflits.
    // Si une session existe déjà, recopier le téléphone optionnel sans bloquer l'inscription.
    if (data.phone && authData.session) {
      const { error: phoneError } = await supabase
        .from("profiles")
        .update({ phone: data.phone })
        .eq("id", authData.user.id);

      if (phoneError && import.meta.env.DEV) {
        console.warn("[Auth] Optional phone was kept in metadata:", phoneError.message);
      }
    }

    return {
      user: authData.user,
      session: authData.session,
    };
  },

  /**
   * Sign in existing user
   * Vérifie que l'email est vérifié avant de permettre la connexion
   * Phase 2.1 : Utilise retryAuthOperation pour centraliser la logique de retry
   */
  async signIn(data: SignInData) {
    try {
      // Utiliser retryAuthOperation pour gérer les retries automatiquement
      const { data: authData, error } = await retryAuthOperation(() =>
        supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        })
      );

      if (error) {
        const context: ErrorContext = {
          action: "signIn",
          component: "authService",
          email: data.email,
        };
        handleError(error, context, { showToast: false, silent: false });

        // Utiliser une clé i18n pour que l'UI affiche t(key)
        const errorKey = getSupabaseErrorKey(error, "errors.auth.loginFailed");
        throw new Error(errorKey);
      }

      // Enhanced verification check
      if (authData.user) {
        // Check email verification
        if (!isEmailVerified(authData.user)) {
          // Déconnecter l'utilisateur si l'email n'est pas vérifié
          await supabase.auth.signOut();
          throw new Error("EMAIL_NOT_VERIFIED");
        }
        
        // Additional check: Verify profile sync (optional but recommended)
        // This ensures consistency between auth.users and profiles
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("is_email_verified")
            .eq("id", authData.user.id)
            .single();
          
          // If profile exists and is_email_verified is false but email_confirmed_at is set,
          // trigger sync (should be handled by trigger, but this is a safety check)
          if (profile && !profile.is_email_verified && authData.user.email_confirmed_at) {
            // Log warning - trigger should have handled this
            if (import.meta.env.DEV) {
              console.warn(
                "[Auth] Email verification sync issue detected. " +
                "email_confirmed_at is set but is_email_verified is false. " +
                "Trigger should have synced this."
              );
            }
            
            // Attempt to sync manually as a safety measure
            // The trigger should have handled this, but we do it here as a fallback
            await supabase
              .from("profiles")
              .update({ is_email_verified: true, updated_at: new Date().toISOString() })
              .eq("id", authData.user.id);
          }
        } catch (err) {
          // Non-critical check, continue with login
          logAuthError(
            "Error checking profile verification during sign in",
            createLogContext(authData.user.id, "signIn", "authService"),
            err instanceof Error ? err : undefined
          );
        }
      }

      // Annuler la suppression programmée si l'utilisateur se reconnecte
      if (authData.user) {
          try {
            await accountDeletionService.cancelDeletionIfScheduled();
          } catch (error) {
            // Silently fail - non-critical operation
            logAuthError(
              "Error cancelling scheduled deletion",
              createLogContext(authData.user?.id, "signIn", "authService"),
              error instanceof Error ? error : undefined
            );
          }
      }

      return {
        user: authData.user,
        session: authData.session,
      };
    } catch (err) {
      // Si c'est déjà une Error, la relancer
      if (err instanceof Error) {
        throw err;
      }
      // Sinon, créer une nouvelle Error
      throw new Error(String(err));
    }
  },

  /**
   * Sign out user
   * Phase 2.2 : Utilise clearAuthSession() pour nettoyer complètement la session
   */
  async signOut() {
    try {
      // Utiliser clearAuthSession() pour nettoyer complètement
      const cleared = await clearAuthSession();
      if (!cleared) {
        // Si clearAuthSession échoue, essayer quand même signOut standard
        const { error } = await supabase.auth.signOut();
        if (error) {
          const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la déconnexion");
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la déconnexion");
      throw new Error(errorMessage);
    }
  },

  /**
   * Get current session
   * Phase 2.1 : Utilise retrySessionOperation pour centraliser la logique de retry
   */
  async getSession() {
    try {
      const { data, error } = await retrySessionOperation(() => supabase.auth.getSession());

      if (error) {
        const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la récupération de la session");
        throw new Error(errorMessage);
      }

      return data.session;
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error(String(err));
    }
  },

  /**
   * Get current user
   */
  async getCurrentUser() {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) {
      const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la récupération de l'utilisateur");
      throw new Error(errorMessage);
    }
    return user;
  },

  /**
   * Request password reset
   * Utilise /auth/confirm pour le flow PKCE avec type=recovery
   */
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // Pour le flow PKCE, rediriger vers /auth/confirm qui échangera le token
      // Le type=recovery sera ajouté automatiquement par Supabase
      redirectTo: `${getBaseUrl()}/auth/confirm?next=/auth/change-password`,
    });

    if (error) {
      const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la réinitialisation du mot de passe");
      throw new Error(errorMessage);
    }
  },

  /**
   * Update password
   */
  async updatePassword(password: string) {
    const { error } = await supabase.auth.updateUser({
      password,
    });

    if (error) {
      const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de la mise à jour du mot de passe");
      throw new Error(errorMessage);
    }
  },

  /**
   * Resend verification email
   * Utilise /auth/confirm pour le flow PKCE
   * Implements rate limiting: max 3 emails per hour per email address
   */
  async resendVerificationEmail(email: string) {
    // Check rate limit (max 3 emails per hour per email)
    // Use localStorage for client-side rate limiting (simple implementation)
    // In production, this should be enforced server-side via Edge Function or database
    const rateLimitKey = `verification_email:${email}`;
    const rateLimitData = localStorage.getItem(rateLimitKey);
    
    if (rateLimitData) {
      const { count, firstRequestTime } = JSON.parse(rateLimitData);
      const now = Date.now();
      const oneHour = 3600 * 1000; // 1 hour in milliseconds
      
      // Reset if more than 1 hour has passed
      if (now - firstRequestTime > oneHour) {
        localStorage.setItem(rateLimitKey, JSON.stringify({ count: 1, firstRequestTime: now }));
      } else if (count >= 3) {
        // Rate limit exceeded
        const retryAfter = Math.ceil((oneHour - (now - firstRequestTime)) / 1000); // seconds
        throw new Error(
          `Trop de demandes. Veuillez attendre ${Math.ceil(retryAfter / 60)} minutes avant de redemander un email de vérification.`
        );
      } else {
        // Increment count
        localStorage.setItem(rateLimitKey, JSON.stringify({ count: count + 1, firstRequestTime }));
      }
    } else {
      // First request
      localStorage.setItem(rateLimitKey, JSON.stringify({ count: 1, firstRequestTime: Date.now() }));
    }

    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email,
      options: {
        emailRedirectTo: buildEmailConfirmRedirect(getBaseUrl(), recallPostVerifyRedirect()),
      },
    });

    if (error) {
      const errorMessage = getSupabaseErrorMessage(error, "Erreur lors de l'envoi de l'email de vérification");
      throw new Error(errorMessage);
    }
  },

  /**
   * Check login attempts rate limiting
   */
  async checkLoginAttempts(email: string) {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/check-login-attempts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ email }),
    });

    // Vérifier le Content-Type avant de parser
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    if (!response.ok) {
      // Essayer de parser le JSON, sinon utiliser le texte brut
      let errorMessage = "Failed to check login attempts";
      try {
        if (isJson) {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } else {
          const text = await response.text();
          // Si c'est du HTML, extraire un message d'erreur générique
          if (text.trim().startsWith("<")) {
            errorMessage = `Erreur serveur (${response.status}). Veuillez réessayer.`;
          } else {
            errorMessage = text || errorMessage;
          }
        }
      } catch (parseError) {
        // Si le parsing échoue, utiliser un message d'erreur générique
        if (import.meta.env.DEV) {
          console.error("[Auth] Error parsing error response:", parseError);
        }
        errorMessage = `Erreur serveur (${response.status}). Veuillez réessayer.`;
      }
      throw new Error(errorMessage);
    }

    // Parser la réponse JSON avec gestion d'erreur
    // Toujours lire le corps en texte d'abord pour éviter "Unexpected token '<'"
    // quand le serveur renvoie du HTML (proxy, erreur, mauvais Content-Type)
    try {
      const text = await response.text();
      if (text.trim().startsWith("<")) {
        if (import.meta.env.DEV) {
          console.warn("[Auth] Response body is HTML, returning default rate limit check");
        }
        return {
          allowed: true,
          attemptsRemaining: 5,
          maxAttempts: 5,
        };
      }
      try {
        return JSON.parse(text) as { allowed: boolean; attemptsRemaining: number; maxAttempts: number; blockedUntil?: string; message?: string };
      } catch {
        if (import.meta.env.DEV) {
          console.warn("[Auth] Response is not valid JSON, returning default rate limit check");
        }
        return {
          allowed: true,
          attemptsRemaining: 5,
          maxAttempts: 5,
        };
      }
    } catch (parseError) {
      // En cas d'erreur de parsing, retourner une réponse par défaut qui permet la connexion
      if (import.meta.env.DEV) {
        console.error("[Auth] Error parsing response:", parseError);
      }
      return {
        allowed: true,
        attemptsRemaining: 5,
        maxAttempts: 5,
      };
    }
  },

  /**
   * Record a login attempt
   */
  async recordLoginAttempt(email: string, success: boolean, ipAddress?: string) {
    const { error } = await supabase.rpc("record_login_attempt", {
      p_email: email,
      p_ip_address: ipAddress ?? undefined,
      p_success: success,
    });

    if (error) {
      const context: ErrorContext = {
        action: "recordLoginAttempt",
        component: "authService",
        email: email,
      };
      handleError(error, context, { showToast: false, silent: true }); // Silent car non-critique
    }
  },

  /**
   * Reset login attempts after successful login
   */
  async resetLoginAttempts(email: string) {
    const { error } = await supabase.rpc("reset_login_attempts", {
      p_email: email,
    });

    if (error) {
      const context: ErrorContext = {
        action: "resetLoginAttempts",
        component: "authService",
        email: email,
      };
      handleError(error, context, { showToast: false, silent: true }); // Silent car non-critique
    }
  },

  /**
   * Sign in with Google OAuth
   *
   * Valide l'URL de redirection avant l'appel et log les informations de debug en développement.
   * Gère les erreurs OAuth avec des messages clairs et actionnables.
   *
   * @param role - Rôle optionnel à pré-remplir (pour l'inscription)
   * @returns Promise avec les données OAuth
   * @throws Error avec message clair si problème de configuration ou d'authentification
   */
  async signInWithGoogle(role?: "renter" | "owner") {
    // Construire l'URL de redirection
    const baseUrl = getBaseUrl();
    let redirectTo = `${baseUrl}/auth/callback`;

    // EN DÉVELOPPEMENT : Forcer l'utilisation de localhost même si VITE_APP_URL est défini
    // Vérifier que l'URL ne pointe pas vers Vercel ou un autre domaine
    if (import.meta.env.DEV) {
      const currentOrigin = typeof window !== "undefined" ? window.location.origin : null;
      if (currentOrigin && !redirectTo.startsWith(currentOrigin)) {
        // Si l'URL de redirection ne correspond pas à l'origine actuelle, utiliser l'origine
        redirectTo = `${currentOrigin}/auth/callback`;
        console.warn(
          "[Auth] DEV mode: Redirect URL was pointing to different domain, forcing localhost:",
          redirectTo
        );
      }

      // Vérification supplémentaire : s'assurer que c'est bien localhost
      try {
        const redirectUrl = new URL(redirectTo);
        if (!redirectUrl.hostname.includes("localhost") && !redirectUrl.hostname.includes("127.0.0.1")) {
          // Forcer localhost si ce n'est pas déjà le cas
          const localOrigin = currentOrigin || "http://localhost:5173";
          redirectTo = `${localOrigin}/auth/callback`;
          console.warn(
            "[Auth] DEV mode: Redirect URL was not localhost, forcing:",
            redirectTo
          );
        }
      } catch {
        // Si l'URL n'est pas valide, utiliser l'origine actuelle
        if (currentOrigin) {
          redirectTo = `${currentOrigin}/auth/callback`;
        }
      }
    }

    // Validation préventive de l'URL de redirection
    try {
      const redirectUrl = new URL(redirectTo);
      if (!redirectUrl.protocol.match(/^https?:$/)) {
        throw new Error(`Invalid protocol: ${redirectUrl.protocol}`);
      }
    } catch (urlError) {
      const errorMsg = `URL de redirection invalide: ${redirectTo}. Vérifiez votre configuration.`;
      if (import.meta.env.DEV) {
        console.error("[Auth] Invalid redirect URL:", redirectTo, urlError);
        console.error("[Auth] Base URL was:", baseUrl);
      }
      throw new Error(errorMsg);
    }

    // The OAuth redirect cannot carry ?role= (allow-list is the bare callback URL).
    // sessionStorage survives the same-tab return so an agency signup stays owner.
    rememberSignupRole(role);

    // Logger les informations de debug en développement
    if (import.meta.env.DEV) {
      console.log("[Auth] Initiating Google OAuth sign in");
      console.log("[Auth] Final redirect URL (forced localhost in DEV):", redirectTo);
      console.log("[Auth] Current window.location.origin:", typeof window !== "undefined" ? window.location.origin : "N/A");
      console.log("[Auth] Role:", role || "not specified");
    }

    // Appel OAuth avec validation
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });

    if (error) {
      rememberSignupRole(null);
      // Logger l'erreur complète en dev pour faciliter le debugging
      if (import.meta.env.DEV) {
        console.error("[Auth] Google OAuth error:", {
          message: error.message,
          code: (error as { code?: string }).code,
          status: (error as { status?: number }).status,
          redirectTo,
        });
      }

      const context: ErrorContext = {
        action: "signInWithGoogle",
        component: "authService",
        role: role,
      };
      handleError(error, context, { showToast: false, silent: false });

      // Obtenir un message d'erreur clair et actionnable
      const errorMessage = getSupabaseErrorMessage(
        error,
        `Erreur lors de la connexion Google. URL de redirection: ${redirectTo}`
      );

      // Améliorer le message si c'est un problème de provider non activé
      const errorCode = (error as { code?: string }).code;
      const normalizedMessage = error.message?.toLowerCase() || "";
      if (
        errorCode === "validation_failed" &&
        (normalizedMessage.includes("unsupported provider") ||
          normalizedMessage.includes("provider is not enabled"))
      ) {
        throw new Error(
          `Le fournisseur Google OAuth n'est pas activé dans Supabase. ` +
          `Vérifiez la configuration dans le Dashboard Supabase (Authentication > Providers > Google). ` +
          `URL de redirection utilisée: ${redirectTo}`
        );
      }

      throw new Error(errorMessage);
    }

    // Logger le succès en dev
    if (import.meta.env.DEV) {
      console.log("[Auth] Google OAuth initiated successfully, redirecting to:", data.url);
    }

    return data;
  },

  /**
   * Détecte si la réponse de signUp est une réponse obfusquée
   *
   * Supabase retourne une réponse obfusquée (sans erreur mais sans créer de compte)
   * lorsqu'un email existe déjà avec un autre provider (OAuth). Cela prévient les
   * attaques d'énumération d'utilisateurs mais cause confusion pour les vrais utilisateurs.
   *
   * @param user - L'utilisateur retourné par signUp
   * @param email - L'email utilisé pour l'inscription
   * @returns true si c'est une réponse obfusquée (email déjà utilisé)
   */
  isObfuscatedSignUpResponse(user: { identities?: Array<{ provider?: string; id?: string }> | null; id?: string }, email: string): boolean {
    // Cas 1: Pas d'identités du tout - réponse obfusquée
    if (!user.identities || user.identities.length === 0) {
      return true;
    }

    // Cas 2: L'identité email n'existe pas dans les identités
    // Si on vient de créer un compte email, il devrait y avoir une identité 'email'
    const hasEmailIdentity = user.identities.some(
      (identity) => identity.provider === "email"
    );

    if (!hasEmailIdentity) {
      // L'utilisateur a des identités mais pas 'email' - probablement un compte OAuth existant
      return true;
    }

    // Cas 3: L'identité email existe mais l'id ne correspond pas à l'utilisateur
    // (cas rare mais possible lors de problèmes de synchronisation)
    const emailIdentity = user.identities.find((identity) => identity.provider === "email");
    if (emailIdentity && emailIdentity.id !== user.id) {
      return true;
    }

    return false;
  },
};
